import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import TransferRequestModal from '../components/TransferRequestModal';
import TransferApprovalModal from '../components/TransferApprovalModal';
import TransferDetailsModal from '../components/TransferDetailsModal';
import TransferHistoryTable from '../components/TransferHistoryTable';
import { getTransfers, getTransferStats } from '../services/transfersAPI';

const TransfersPage = () => {
  const { user, isAdmin, isManager } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [transfers, setTransfers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  useEffect(() => {
    fetchTransfers();
    if (isManager || isAdmin) {
      fetchStats();
    }
  }, [activeTab]);

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      let filter = {};
      
      // Filter based on active tab
      if (activeTab === 0) {
        filter.status = 'pending'; // Pending transfers
      } else if (activeTab === 1) {
        // My transfers (for supervisors)
        filter = {}; // API will filter by requestedBy
      }
      // activeTab === 2 is "All Transfers" - no filter

      const data = await getTransfers(filter);
      setTransfers(data);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await getTransferStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCreateTransfer = () => {
    setRequestModalOpen(true);
  };

  const handleViewTransfer = (transfer) => {
    setSelectedTransfer(transfer);
    setDetailsModalOpen(true);
  };

  const handleApproveClick = (transfer) => {
    setSelectedTransfer(transfer);
    setApprovalModalOpen(true);
  };

  const handleTransferCreated = () => {
    setRequestModalOpen(false);
    fetchTransfers();
    if (isManager || isAdmin) fetchStats();
  };

  const handleTransferApproved = () => {
    setApprovalModalOpen(false);
    fetchTransfers();
    if (isManager || isAdmin) fetchStats();
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'warning' },
      completed: { label: 'Completed', color: 'success' },
      rejected: { label: 'Rejected', color: 'error' },
      cancelled: { label: 'Cancelled', color: 'default' }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" gutterBottom>
            Material Transfers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Transfer materials between sites
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTransfer}
        >
          Request Transfer
        </Button>
      </Box>

      {/* Stats Cards (Manager/Admin only) */}
      {(isManager || isAdmin) && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Pending</Typography>
            <Typography variant="h4">{stats.pending || 0}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Completed</Typography>
            <Typography variant="h4" color="success.main">{stats.completed || 0}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Rejected</Typography>
            <Typography variant="h4" color="error.main">{stats.rejected || 0}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h4">{stats.total || 0}</Typography>
          </Paper>
        </Box>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={`Pending (${stats.pending || 0})`} />
          <Tab label="My Transfers" />
          {(isManager || isAdmin) && <Tab label="All Transfers" />}
        </Tabs>
      </Paper>

      {/* Transfer Table */}
      <TransferHistoryTable
        transfers={transfers}
        loading={loading}
        onView={handleViewTransfer}
        onApprove={isManager || isAdmin ? handleApproveClick : null}
        onRefresh={fetchTransfers}
      />

      {/* Modals */}
      <TransferRequestModal
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onSuccess={handleTransferCreated}
      />

      <TransferApprovalModal
        open={approvalModalOpen}
        transfer={selectedTransfer}
        onClose={() => setApprovalModalOpen(false)}
        onSuccess={handleTransferApproved}
      />

      <TransferDetailsModal
        open={detailsModalOpen}
        transfer={selectedTransfer}
        onClose={() => setDetailsModalOpen(false)}
      />
    </Box>
  );
};

export default TransfersPage;
